import { useState } from "react";
import { useCreateEvent, useDeleteEvent, useTerminEvents, useUpdateEvent } from "../../hooks/usePlanung";
import "../../styles/termin.css";

/* ══ Terminvorbereitung ═════════════════════════════════════════════════
 *
 * Was muss an diesem Tag bereitstehen — „20 t Mineralgemisch bestellen",
 * „Bagger bestellen". Gedacht als Orientierung beim Planen: woran müssen wir
 * denken. Deshalb nur ein Satz und ein Haken, gepflegt wie die Punkte einer
 * Aufgabe (Kreis antippen = erledigt).
 *
 * Bis 05.08.2026 verlangte der Bereich Kategorie, Uhrzeit und Kosten; bis
 * 13.10.2026 noch einen Lieferanten. Der fällt weg (Benjamin: „Es muss auch kein
 * Lieferant hinzugefügt werden"). Die Spalte `lieferant_id` bleibt — wo einer
 * steht, wird er leise mit angezeigt.
 */

/** Kurzfassung für den zugeklappten Kopf: Zahl offener Punkte und die ersten davon. */
export function useVorbereitungLage(terminId: string, datum: string) {
  const { data: events = [], isPending } = useTerminEvents(terminId, datum);
  const offen = events.filter((e) => !e.erledigt);
  return {
    /* Erst wenn geladen, entscheidet sich, ob der Bereich offen steht. */
    geladen: !isPending,
    offen: offen.length,
    gesamt: events.length,
    vorschau: offen.length
      ? offen.slice(0, 3).map((e) => e.titel).join(" · ")
      : events.length ? "alles erledigt" : "noch nichts eingetragen",
  };
}

export function TerminVorbereitung({ terminId, datum }: { terminId: string; datum: string }) {
  const { data: events = [] } = useTerminEvents(terminId, datum);
  const create = useCreateEvent();
  const upd = useUpdateEvent();
  const del = useDeleteEvent();
  const [titel, setTitel] = useState("");
  const add = () => {
    if (!titel.trim() || create.isPending) return;
    create.mutate({ termin_id: terminId, titel: titel.trim(), lieferant_id: "" }, { onSuccess: () => setTitel("") });
  };
  // Offene oben, erledigte darunter — die Reihenfolge innerhalb bleibt.
  const sortiert = [...events.filter((e) => !e.erledigt), ...events.filter((e) => e.erledigt)];
  return (
    <div className="tm-vorb">
      {sortiert.length > 0 && (
        <ul className="tm-vorb-liste">
          {sortiert.map((e) => (
            <li key={e.id} className={e.erledigt ? "is-erledigt" : ""}>
              <button
                type="button"
                className="tm-haken"
                aria-pressed={e.erledigt}
                aria-label={e.erledigt ? `${e.titel} — wieder offen` : `${e.titel} — erledigt`}
                onClick={() => upd.mutate({ id: e.id, patch: { erledigt: !e.erledigt } })}
              >
                <span className="tm-haken-kreis" aria-hidden="true">
                  {e.erledigt && <svg viewBox="0 0 16 16" width="12" height="12"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </span>
              </button>
              <span className="tm-vorb-text">
                {e.titel}
                {e.lieferant_name && <small> · {e.lieferant_name}</small>}
              </span>
              <button type="button" className="tm-weg" onClick={() => del.mutate(e.id)} aria-label={`${e.titel} entfernen`} title="Entfernen">×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="tm-vorb-neu">
        <input
          className="tm-feld"
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={events.length ? "+ Punkt" : "z. B. „20 t Mineralgemisch bestellen“"}
          aria-label="Was muss da sein?"
        />
        <button type="button" className="tm-dazu" onClick={add} disabled={!titel.trim() || create.isPending}>Dazu</button>
      </div>
    </div>
  );
}
